import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  ArrowLeft,
  ArrowUpDown,
  Building2,
  Layers,
  Network,
  Search,
  ShieldAlert,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types — mirror IssuerExposureService DTOs
// ---------------------------------------------------------------------------

interface IssuerExposureSummary {
  issuerKey: string;
  displayName: string;
  sector: string | null;
  issuerId: number | null;
  totalMarketValue: number;
  totalCostBasis: number;
  totalUnrealizedPnl: number;
  positionCount: number;
  vehicleTypes: string[];
  exposureByAssetClass: Record<string, number>;
}

interface IssuerPositionRow {
  assetClass: string;
  vehicleType: string;
  portfolioName: string;
  securityId: number | null;
  cusip: string | null;
  securityName: string;
  quantity: number;
  costBasis: number;
  marketValue: number;
  unrealizedPnl: number;
  asOfDate: string;
  source: string;
}

interface IssuerExposureDetail {
  issuerKey: string;
  displayName: string;
  sector: string | null;
  issuerId: number | null;
  totalMarketValue: number;
  totalCostBasis: number;
  totalUnrealizedPnl: number;
  vehicleCount: number;
  exposureByAssetClass: Record<string, number>;
  positions: IssuerPositionRow[];
}

// ---------------------------------------------------------------------------
// Styled components — institutional dark theme (PGIM/Aladdin tier)
// ---------------------------------------------------------------------------

const Container = styled.div`
  max-width: 1800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  gap: 1.5rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Crumb = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #60a5fa;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-bottom: 0.5rem;

  &:hover { color: #93c5fd; }
`;

const Title = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  font-size: 0.85rem;
  margin: 0;
  max-width: 720px;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 0.5rem 0.85rem;
  width: 280px;

  input {
    background: none;
    border: none;
    outline: none;
    color: white;
    font-size: 0.85rem;
    width: 100%;
  }

  svg { color: #64748b; }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled(motion.div)<{ $accent: string }>`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${p => p.$accent};
  }
`;

const StatLabel = styled.div`
  color: #94a3b8;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.35rem;
`;

const StatValue = styled.div`
  color: white;
  font-size: 1.4rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const StatHint = styled.div`
  color: #64748b;
  font-size: 0.72rem;
  margin-top: 0.2rem;
`;

const Panel = styled.div`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  padding: 0;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const PanelTitle = styled.h3`
  color: white;
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PanelHint = styled.span`
  color: #64748b;
  font-size: 0.72rem;
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 980px;
`;

const Th = styled.th<{ $numeric?: boolean; $sortable?: boolean }>`
  color: #94a3b8;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0.75rem 1rem;
  text-align: ${p => p.$numeric ? 'right' : 'left'};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(15, 23, 42, 0.4);
  cursor: ${p => p.$sortable ? 'pointer' : 'default'};
  user-select: none;
  white-space: nowrap;

  &:hover { color: ${p => p.$sortable ? '#e2e8f0' : '#94a3b8'}; }
`;

const SortIcon = styled(ArrowUpDown)`
  width: 12px;
  height: 12px;
  margin-left: 4px;
  display: inline-block;
  vertical-align: middle;
  opacity: 0.5;
`;

const Tr = styled.tr<{ $clickable?: boolean }>`
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};
  transition: background 0.15s;

  &:hover {
    background: ${p => p.$clickable ? 'rgba(59, 130, 246, 0.08)' : 'transparent'};
  }
`;

const Td = styled.td<{ $numeric?: boolean; $muted?: boolean }>`
  color: ${p => p.$muted ? '#94a3b8' : '#e2e8f0'};
  font-size: 0.82rem;
  padding: 0.7rem 1rem;
  text-align: ${p => p.$numeric ? 'right' : 'left'};
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const IssuerCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const IssuerName = styled.span`
  color: white;
  font-weight: 600;
`;

const IssuerSub = styled.span`
  color: #64748b;
  font-size: 0.7rem;
`;

const ChipRow = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const VehicleChip = styled.span<{ $accent: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  background: ${p => p.$accent}1f;
  color: ${p => p.$accent};
  border: 1px solid ${p => p.$accent}55;
`;

const Pnl = styled.span<{ $value: number }>`
  color: ${p => p.$value > 0 ? '#10b981' : p.$value < 0 ? '#ef4444' : '#94a3b8'};
  font-weight: 600;
`;

const EmptyState = styled.div`
  color: #64748b;
  font-size: 0.9rem;
  padding: 3rem 2rem;
  text-align: center;
`;

const ErrorState = styled.div`
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 10px;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
  font-size: 0.85rem;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.25rem;
  margin-bottom: 1.5rem;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Bar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0.5rem 0;
`;

const BarHead = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.78rem;

  span:first-child { color: #e2e8f0; font-weight: 500; }
  span:last-child  { color: #94a3b8; font-variant-numeric: tabular-nums; }
`;

const BarTrack = styled.div`
  height: 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.05);
  overflow: hidden;
`;

const BarFill = styled.div<{ $pct: number; $accent: string }>`
  height: 100%;
  width: ${p => Math.min(100, Math.max(0, p.$pct))}%;
  background: ${p => p.$accent};
`;

// ---------------------------------------------------------------------------
// Vehicle taxonomy — keeps colors stable across screens
// ---------------------------------------------------------------------------

const VEHICLE_ACCENT: Record<string, string> = {
  LEVERAGED_LOAN: '#3b82f6',
  CLO: '#8b5cf6',
  CDS: '#f97316',
  PRIVATE_CREDIT: '#10b981',
  REAL_ESTATE_DEBT: '#ec4899',
  REAL_ESTATE_EQUITY: '#06b6d4',
  PRIVATE_EQUITY: '#a78bfa',
  CMBS: '#84cc16',
  DISTRESSED: '#ef4444',
  BOND: '#64748b',
  OTHER: '#475569',
};

const VEHICLE_LABEL: Record<string, string> = {
  LEVERAGED_LOAN: 'LL',
  CLO: 'CLO',
  CDS: 'CDS',
  PRIVATE_CREDIT: 'PC',
  REAL_ESTATE_DEBT: 'RE-D',
  REAL_ESTATE_EQUITY: 'RE-E',
  PRIVATE_EQUITY: 'PE',
  CMBS: 'CMBS',
  DISTRESSED: 'DSTR',
  BOND: 'BOND',
  OTHER: 'OTHER',
};

const accentFor = (v: string) => VEHICLE_ACCENT[v] || '#64748b';
const labelFor  = (v: string) => VEHICLE_LABEL[v]  || v;

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

const fmtUsd = (n: number | null | undefined): string => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};

const fmtUsdSigned = (n: number | null | undefined): string => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const sign = n >= 0 ? '+' : '-';
  return sign + fmtUsd(Math.abs(n));
};

const fmtPct = (a: number, b: number): string => {
  if (!b || isNaN(b)) return '—';
  return `${((a / b) * 100).toFixed(1)}%`;
};

// ---------------------------------------------------------------------------
// Sort state
// ---------------------------------------------------------------------------

type SortKey = 'name' | 'mv' | 'cost' | 'pnl' | 'vehicles' | 'positions';
type SortDir = 'asc' | 'desc';

// ===========================================================================
// MAIN COMPONENT
// ===========================================================================

const CrossVehicleExposureScreen: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const issuerKey = searchParams.get('issuer');

  if (issuerKey) {
    return (
      <IssuerDetailView
        issuerKey={issuerKey}
        onBack={() => {
          const next = new URLSearchParams(searchParams);
          next.delete('issuer');
          setSearchParams(next, { replace: true });
        }}
      />
    );
  }

  return (
    <ExposureMatrixView
      onSelectIssuer={(key) => {
        const next = new URLSearchParams(searchParams);
        next.set('issuer', key);
        setSearchParams(next, { replace: true });
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// Matrix view (list of issuers)
// ---------------------------------------------------------------------------

const ExposureMatrixView: React.FC<{ onSelectIssuer: (key: string) => void }> = ({ onSelectIssuer }) => {
  const [rows, setRows] = useState<IssuerExposureSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('mv');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    let alive = true;
    setErr(null);
    axios.get<IssuerExposureSummary[]>('/api/credit/exposure/issuers')
      .then(res => { if (alive) setRows(res.data || []); })
      .catch(e => {
        if (alive) setErr(e?.response?.data?.message || e.message || 'Failed to load exposure');
      });
    return () => { alive = false; };
  }, []);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir(k === 'name' ? 'asc' : 'desc');
    }
  };

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    let out = q
      ? rows.filter(r =>
          r.displayName.toLowerCase().includes(q) ||
          r.issuerKey.toLowerCase().includes(q) ||
          (r.sector || '').toLowerCase().includes(q)
        )
      : rows.slice();

    const dir = sortDir === 'asc' ? 1 : -1;
    out.sort((a, b) => {
      switch (sortKey) {
        case 'name':      return dir * a.displayName.localeCompare(b.displayName);
        case 'mv':        return dir * (a.totalMarketValue - b.totalMarketValue);
        case 'cost':      return dir * (a.totalCostBasis - b.totalCostBasis);
        case 'pnl':       return dir * (a.totalUnrealizedPnl - b.totalUnrealizedPnl);
        case 'vehicles':  return dir * (a.vehicleTypes.length - b.vehicleTypes.length);
        case 'positions': return dir * (a.positionCount - b.positionCount);
      }
    });
    return out;
  }, [rows, search, sortKey, sortDir]);

  // Aggregate stats
  const stats = useMemo(() => {
    if (!rows) return null;
    const totalMv = rows.reduce((s, r) => s + (r.totalMarketValue || 0), 0);
    const totalCost = rows.reduce((s, r) => s + (r.totalCostBasis || 0), 0);
    const multiVehicle = rows.filter(r => r.vehicleTypes.length >= 2).length;
    const distinctVehicles = new Set<string>();
    rows.forEach(r => r.vehicleTypes.forEach(v => distinctVehicles.add(v)));
    const top = rows.slice().sort((a, b) => b.totalMarketValue - a.totalMarketValue)[0];
    return {
      totalMv,
      totalPnl: totalMv - totalCost,
      issuerCount: rows.length,
      multiVehicle,
      vehicleCount: distinctVehicles.size,
      topIssuer: top?.displayName || '—',
      topIssuerMv: top?.totalMarketValue || 0,
      topIssuerPct: totalMv > 0 ? (top?.totalMarketValue || 0) / totalMv : 0,
    };
  }, [rows]);

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Title>Cross-Vehicle Issuer Exposure</Title>
          <Subtitle>
            Aggregate market value per issuer across leveraged loans, CLO collateral,
            CDS protection, private credit, real estate, and traded bonds. Click an
            issuer to drill into every position in every sleeve.
          </Subtitle>
        </HeaderLeft>
        <SearchBox>
          <Search size={14} />
          <input
            placeholder="Search issuer, sector, or key"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </SearchBox>
      </Header>

      {stats && (
        <StatsGrid>
          <StatCard $accent="#3b82f6" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <StatLabel>Total Market Value</StatLabel>
            <StatValue>{fmtUsd(stats.totalMv)}</StatValue>
            <StatHint>{stats.issuerCount} issuers · {stats.vehicleCount} vehicle types</StatHint>
          </StatCard>
          <StatCard $accent="#10b981" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <StatLabel>Unrealised P&amp;L</StatLabel>
            <StatValue>
              <Pnl $value={stats.totalPnl}>{fmtUsdSigned(stats.totalPnl)}</Pnl>
            </StatValue>
            <StatHint>across all sleeves</StatHint>
          </StatCard>
          <StatCard $accent="#8b5cf6" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <StatLabel>Multi-Vehicle Issuers</StatLabel>
            <StatValue>{stats.multiVehicle}</StatValue>
            <StatHint>held in 2+ vehicle types</StatHint>
          </StatCard>
          <StatCard $accent="#f59e0b" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <StatLabel>Largest Issuer</StatLabel>
            <StatValue style={{ fontSize: '1rem' }}>{stats.topIssuer}</StatValue>
            <StatHint>{fmtUsd(stats.topIssuerMv)} · {(stats.topIssuerPct * 100).toFixed(1)}% of book</StatHint>
          </StatCard>
        </StatsGrid>
      )}

      {err && <ErrorState><ShieldAlert size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />{err}</ErrorState>}

      <Panel>
        <PanelHeader>
          <PanelTitle><Network size={16} />Issuer Roll-Up</PanelTitle>
          <PanelHint>
            {filtered.length} {filtered.length === 1 ? 'issuer' : 'issuers'} shown
            {search && rows ? ` (of ${rows.length})` : ''}
          </PanelHint>
        </PanelHeader>

        {rows === null && <EmptyState>Loading exposure…</EmptyState>}
        {rows !== null && filtered.length === 0 && (
          <EmptyState>
            {search ? 'No issuers match the search.' : 'No issuer exposure on file.'}
          </EmptyState>
        )}

        {rows !== null && filtered.length > 0 && (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th $sortable onClick={() => toggleSort('name')}>Issuer<SortIcon /></Th>
                  <Th>Sector</Th>
                  <Th $sortable onClick={() => toggleSort('vehicles')}>Vehicles<SortIcon /></Th>
                  <Th $numeric $sortable onClick={() => toggleSort('positions')}>Positions<SortIcon /></Th>
                  <Th $numeric $sortable onClick={() => toggleSort('mv')}>Market Value<SortIcon /></Th>
                  <Th $numeric $sortable onClick={() => toggleSort('cost')}>Cost Basis<SortIcon /></Th>
                  <Th $numeric $sortable onClick={() => toggleSort('pnl')}>Unrealised P&amp;L<SortIcon /></Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Tr key={r.issuerKey} $clickable onClick={() => onSelectIssuer(r.issuerKey)}>
                    <Td>
                      <IssuerCell>
                        <IssuerName>{r.displayName}</IssuerName>
                        <IssuerSub>{r.issuerKey}</IssuerSub>
                      </IssuerCell>
                    </Td>
                    <Td $muted>{r.sector || '—'}</Td>
                    <Td>
                      <ChipRow>
                        {r.vehicleTypes.map(v => (
                          <VehicleChip key={v} $accent={accentFor(v)} title={v}>{labelFor(v)}</VehicleChip>
                        ))}
                      </ChipRow>
                    </Td>
                    <Td $numeric $muted>{r.positionCount}</Td>
                    <Td $numeric>{fmtUsd(r.totalMarketValue)}</Td>
                    <Td $numeric $muted>{fmtUsd(r.totalCostBasis)}</Td>
                    <Td $numeric><Pnl $value={r.totalUnrealizedPnl}>{fmtUsdSigned(r.totalUnrealizedPnl)}</Pnl></Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </Panel>
    </Container>
  );
};

// ---------------------------------------------------------------------------
// Detail view (one issuer)
// ---------------------------------------------------------------------------

const IssuerDetailView: React.FC<{ issuerKey: string; onBack: () => void }> = ({ issuerKey, onBack }) => {
  const [detail, setDetail] = useState<IssuerExposureDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setDetail(null);
    setErr(null);
    axios.get<IssuerExposureDetail>(`/api/credit/exposure/issuers/${encodeURIComponent(issuerKey)}`)
      .then(res => { if (alive) setDetail(res.data); })
      .catch(e => {
        if (alive) setErr(e?.response?.status === 404
          ? 'Issuer not found.'
          : e?.response?.data?.message || e.message || 'Failed to load issuer detail');
      });
    return () => { alive = false; };
  }, [issuerKey]);

  const breakdown = useMemo(() => {
    if (!detail) return [];
    const total = detail.totalMarketValue || 0;
    return Object.entries(detail.exposureByAssetClass)
      .sort(([, a], [, b]) => b - a)
      .map(([k, v]) => ({
        assetClass: k,
        mv: v,
        pct: total > 0 ? (v / total) * 100 : 0,
      }));
  }, [detail]);

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Crumb onClick={onBack}><ArrowLeft size={12} />Back to issuer roll-up</Crumb>
          <Title>{detail?.displayName || '…'}</Title>
          <Subtitle>
            {detail?.sector ? `${detail.sector} · ` : ''}
            Cross-asset exposure consolidated by normalised issuer name.
            Each row is one position in one portfolio.
          </Subtitle>
        </HeaderLeft>
      </Header>

      {err && <ErrorState><ShieldAlert size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />{err}</ErrorState>}
      {!detail && !err && <EmptyState>Loading issuer detail…</EmptyState>}

      {detail && (
        <>
          <StatsGrid>
            <StatCard $accent="#3b82f6" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              <StatLabel>Total Market Value</StatLabel>
              <StatValue>{fmtUsd(detail.totalMarketValue)}</StatValue>
              <StatHint>{detail.positions.length} positions · {detail.vehicleCount} vehicle types</StatHint>
            </StatCard>
            <StatCard $accent="#94a3b8" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
              <StatLabel>Total Cost Basis</StatLabel>
              <StatValue>{fmtUsd(detail.totalCostBasis)}</StatValue>
              <StatHint>{fmtPct(detail.totalMarketValue, detail.totalCostBasis)} of cost</StatHint>
            </StatCard>
            <StatCard $accent={detail.totalUnrealizedPnl >= 0 ? '#10b981' : '#ef4444'}
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <StatLabel>Unrealised P&amp;L</StatLabel>
              <StatValue><Pnl $value={detail.totalUnrealizedPnl}>{fmtUsdSigned(detail.totalUnrealizedPnl)}</Pnl></StatValue>
              <StatHint>{detail.totalCostBasis > 0
                ? `${(detail.totalUnrealizedPnl / detail.totalCostBasis * 100).toFixed(1)}% return`
                : '—'}</StatHint>
            </StatCard>
            <StatCard $accent="#8b5cf6" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <StatLabel>Asset Classes</StatLabel>
              <StatValue>{Object.keys(detail.exposureByAssetClass).length}</StatValue>
              <StatHint>distinct credit/structured/private sleeves</StatHint>
            </StatCard>
          </StatsGrid>

          <DetailGrid>
            <Panel>
              <PanelHeader>
                <PanelTitle><Layers size={16} />Positions</PanelTitle>
                <PanelHint>{detail.positions.length} rows</PanelHint>
              </PanelHeader>
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <Th>Asset Class</Th>
                      <Th>Portfolio</Th>
                      <Th>Security</Th>
                      <Th $numeric>Quantity</Th>
                      <Th $numeric>Market Value</Th>
                      <Th $numeric>Unrealised P&amp;L</Th>
                      <Th>As-Of</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.positions.map((p, idx) => (
                      <tr key={`${p.source}-${p.securityId}-${idx}`}>
                        <Td>
                          <ChipRow>
                            <VehicleChip $accent={accentFor(p.vehicleType)} title={p.assetClass}>
                              {labelFor(p.vehicleType)}
                            </VehicleChip>
                          </ChipRow>
                        </Td>
                        <Td $muted>{p.portfolioName || '—'}</Td>
                        <Td>
                          <IssuerCell>
                            <IssuerName style={{ fontSize: '0.82rem' }}>{p.securityName || '—'}</IssuerName>
                            <IssuerSub>{p.cusip || ''}</IssuerSub>
                          </IssuerCell>
                        </Td>
                        <Td $numeric $muted>{p.quantity?.toLocaleString() || '—'}</Td>
                        <Td $numeric>{fmtUsd(p.marketValue)}</Td>
                        <Td $numeric><Pnl $value={p.unrealizedPnl || 0}>{fmtUsdSigned(p.unrealizedPnl)}</Pnl></Td>
                        <Td $muted>{p.asOfDate || '—'}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableScroll>
            </Panel>

            <Panel>
              <PanelHeader>
                <PanelTitle><Building2 size={16} />Asset-Class Breakdown</PanelTitle>
              </PanelHeader>
              <div style={{ padding: '0.5rem 1.5rem 1.25rem' }}>
                {breakdown.map(b => (
                  <Bar key={b.assetClass}>
                    <BarHead>
                      <span>{b.assetClass}</span>
                      <span>{fmtUsd(b.mv)} · {b.pct.toFixed(1)}%</span>
                    </BarHead>
                    <BarTrack>
                      <BarFill $pct={b.pct} $accent="#3b82f6" />
                    </BarTrack>
                  </Bar>
                ))}
                {breakdown.length === 0 && (
                  <div style={{ color: '#64748b', fontSize: '0.85rem', padding: '1rem 0' }}>
                    No asset-class breakdown available.
                  </div>
                )}
              </div>
            </Panel>
          </DetailGrid>
        </>
      )}
    </Container>
  );
};

export default CrossVehicleExposureScreen;
